import React, { useState } from 'react';
import { Model } from '../types/BookingTypes';

const INITIAL_MODEL_COUNT = 18;

interface ModelSelectorProps {
  models: Model[];
  selectedModel: Model | null;
  onSelect: (model: Model) => void;
  searchQuery: string;
  onSearch: (query: string) => void;
  onBack?: () => void;
  brandName?: string;
}

const ModelSelector: React.FC<ModelSelectorProps> = ({
  models,
  selectedModel,
  onSelect,
  searchQuery,
  onSearch,
  onBack,
  brandName
}) => {
  const [showAll, setShowAll] = useState(false);
  const visibleModels = searchQuery || showAll ? models : models.slice(0, INITIAL_MODEL_COUNT);

  return (
    <div>
      {/* Sticky Header and Search Container */}
      <div className="sticky top-0 z-10 bg-gray-100 pb-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-3 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white shadow-sm hover:shadow-md active:scale-95 transition-all"
              >
                <i className="fas fa-arrow-left text-gray-600 text-sm"></i>
              </button>
            )}
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-minto-500 rounded-full" />
              <h3 className="text-base md:text-lg font-semibold text-gray-900">
                Select {brandName} Model
              </h3>
            </div>
          </div>
          <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full shadow-sm">{models.length} models</span>
        </div>

        {/* Search */}
        <div className="relative flex items-center">
          <i className="fas fa-search absolute left-4 text-gray-400 text-sm pointer-events-none"></i>
          <input
            type="text"
            placeholder="Search model..."
            value={searchQuery}
            onChange={e => onSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 md:py-3.5 rounded-xl bg-white border border-gray-100 shadow-sm text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-minto-500/20 focus:border-minto-500 transition-all"
          />
        </div>
      </div>

      {/* Models Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 md:gap-4">
        {visibleModels.map((model) => (
          <button
            key={model.id}
            className={`relative flex flex-col items-center overflow-hidden rounded-lg md:rounded-xl transition-all duration-300 ${
              selectedModel?.id === model.id
                ? 'bg-gradient-to-br from-minto-50 to-blue-50 border-2 border-minto-500 shadow-lg shadow-minto-100'
                : 'bg-white border border-gray-100 shadow-md hover:shadow-lg hover:border-minto-200 hover:-translate-y-0.5 active:scale-95'
            }`}
            onClick={() => onSelect(model)}
          >
            <div className="w-full flex items-center justify-center p-3 md:p-4">
              <div className="w-14 h-14 md:w-20 md:h-20 flex items-center justify-center rounded-lg md:rounded-xl bg-gray-50 p-1 md:p-2">
                <img
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            </div>
            <div className="w-full bg-gray-50 border-t border-gray-100 px-2 py-1">
              <span className="font-bold text-center text-gray-600 leading-tight line-clamp-2 block" style={{ fontSize: '10px' }}>{model.name}</span>
            </div>
            {selectedModel?.id === model.id && (
              <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2">
                <i className="fas fa-check-circle text-minto-500 text-xs md:text-sm"></i>
              </div>
            )}
          </button>
        ))}
      </div>

      {!searchQuery && models.length > INITIAL_MODEL_COUNT && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full mt-3 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-minto-600 hover:text-minto-700 bg-white shadow-sm flex items-center justify-center gap-1.5"
        >
          {showAll ? (
            <>Show Less <i className="fas fa-chevron-up text-xs"></i></>
          ) : (
            <>View All {models.length} Models <i className="fas fa-chevron-down text-xs"></i></>
          )}
        </button>
      )}

      {models.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-white shadow-md">
            <i className="fas fa-search text-gray-400 text-xl"></i>
          </div>
          <p className="text-gray-500 text-sm">No models found matching your search.</p>
        </div>
      )}
    </div>
  );
};

export default ModelSelector; 